<template>
  <modal :name="modalName" width="94%" height="auto" :max-width="860" :max-height="820" :adaptive="true" :scrollable="true"
    :click-to-close="false" @before-close="$emit('close')">
    <section class="communication-template-copy" role="dialog" aria-modal="true" aria-labelledby="communication-template-copy-title">
      <AppModalHeader design="new" eyebrow="Communication" title="Copy a message" title-id="communication-template-copy-title" @close="close" />
      <div class="communication-template-copy__body">
        <p class="communication-template-copy__context">{{ task.client ? task.client.full_name : 'Guest' }} · {{ task.group.name }}</p>
        <div v-if="loading">Loading templates…</div>
        <p v-else-if="error" class="communication-template-copy__error" role="alert">{{ error }} <button type="button" @click="load">Retry</button></p>
        <template v-else-if="templates.length">
          <div class="communication-template-copy__choices">
            <AppFormField design="new" label="Template" label-for="communication-copy-template">
              <AppSelect :value="selectedTemplateId" design="new" input-id="communication-copy-template" aria-label="Template"
                :options="sortedTemplates" :reduce="template => template.id" label="name" placeholder="Select a template"
                :clearable="false" append-to-body :calculate-position="positionTemplateDropdown" @input="onTemplateSelected" />
            </AppFormField>
            <AppFormField v-if="currentTemplate" design="new" label="Language" label-for="communication-copy-language">
              <AppSelect :value="selectedLanguage" design="new" input-id="communication-copy-language" aria-label="Language"
                :options="currentTemplate.texts" :reduce="text => text.language_code" label="language_name"
                :clearable="false" append-to-body :calculate-position="positionTemplateDropdown" @input="onLanguageSelected" />
            </AppFormField>
          </div>
          <template v-if="currentTemplate">
          <p class="communication-template-copy__hint">Check the details before copying. The message is sent outside Board.</p>
          <h3>Message</h3>
          <AppTextarea :value="message" design="new" aria-label="Message text" @input="editMessage" />
          <div class="communication-template-copy__variables-heading">
            <h3>Fill in details</h3>
            <button v-if="manualEdit" type="button" @click="rebuildMessage">Apply table values to message</button>
          </div>
          <p v-if="manualEdit" class="communication-template-copy__hint">You edited the message directly. Table changes will appear after you apply them.</p>
          <p v-if="!currentTemplate.variables.length" class="communication-template-copy__hint">This message has no variables.</p>
          <div v-else class="communication-template-copy__variable-table">
            <div v-for="variable in currentTemplate.variables" :key="variable.key" class="communication-template-copy__variable-row">
              <div><code>{{ placeholderLabel(variable.key) }}</code><span>{{ variable.description }}</span></div>
              <AppInput :value="values[variable.key]" design="new" :aria-label="variable.description"
                :placeholder="variable.example || 'Enter value'" @input="setValue(variable.key, $event)" />
            </div>
          </div>
          <p v-if="hasPlaceholders" class="communication-template-copy__hint">Replace all numbered placeholders before copying.</p>
          <p v-if="copyError" class="communication-template-copy__error" role="alert">{{ copyError }}</p>
          </template>
        </template>
        <p v-else>No active templates are available yet.</p>
      </div>
      <footer class="communication-template-copy__footer">
        <AppButton design="new" theme="dark" @click.native="close">Close</AppButton>
        <AppButton v-if="currentTemplate" design="new" theme="success" :disabled="!canCopy" @click.native="copy">
          <i class="pi pi-copy" aria-hidden="true" /> {{ copied ? 'Copied' : 'Copy message' }}
        </AppButton>
      </footer>
    </section>
  </modal>
</template>

<script>
import { getCommunicationTemplates } from '@/http'
import { positionTemplateDropdown } from './templateSelectDropdown'

const LANGUAGE_CODES = { english: 'us', russian: 'ru', german: 'de', french: 'fr', spanish: 'es' }

export default {
  name: 'CommunicationTemplateCopyModal',
  props: { task: { type: Object, required: true } },
  data: () => ({
    modalName: 'communication-template-copy',
    templates: [],
    selectedTemplateId: null,
    selectedLanguage: '',
    values: {},
    message: '',
    manualEdit: false,
    loading: false,
    error: '',
    copyError: '',
    copied: false
  }),
  computed: {
    currentTemplate() { return this.templates.find(template => template.id === this.selectedTemplateId) || null },
    currentText() { return this.currentTemplate?.texts.find(text => text.language_code === this.selectedLanguage)?.body || '' },
    sortedTemplates() {
      return [...this.templates].sort((a, b) => {
        const aMatch = a.contact_type === this.task.contact_type ? 0 : 1
        const bMatch = b.contact_type === this.task.contact_type ? 0 : 1
        return aMatch - bMatch || a.name.localeCompare(b.name)
      })
    },
    hasPlaceholders() { return /{{[^}]*}}/.test(this.message) },
    canCopy() { return Boolean(this.currentTemplate && this.message.trim() && !this.hasPlaceholders) }
  },
  mounted() { this.$modal.show(this.modalName); this.load() },
  methods: {
    positionTemplateDropdown,
    placeholderLabel(key) { return `{{${key}}}` },
    async load() {
      this.loading = true
      this.error = ''
      try {
        this.templates = (await getCommunicationTemplates()).data.filter(template => template.active)
        this.selectedTemplateId = this.sortedTemplates.find(template => template.contact_type === this.task.contact_type)?.id || null
        this.selectTemplate()
      } catch (error) {
        this.error = 'Could not load templates.'
      } finally {
        this.loading = false
      }
    },
    selectTemplate() {
      if (!this.currentTemplate) return
      const groupLanguage = LANGUAGE_CODES[String(this.task.group.language || '').trim().toLowerCase()]
      this.selectedLanguage = this.currentTemplate.texts.find(text => text.language_code === groupLanguage)?.language_code
        || this.currentTemplate.texts.find(text => text.language_code === 'us')?.language_code
        || this.currentTemplate.texts[0]?.language_code || ''
      this.selectLanguage()
    },
    onTemplateSelected(id) { this.selectedTemplateId = id; this.selectTemplate() },
    onLanguageSelected(code) { this.selectedLanguage = code; this.selectLanguage() },
    selectLanguage() {
      this.values = {}
      for (const variable of this.currentTemplate.variables) {
        const firstName = String(this.task.client?.full_name || '').trim().split(/\s+/)[0] || ''
        const value = variable.key === '1' && variable.description === 'Guest first name' ? firstName : ''
        this.$set(this.values, variable.key, value)
      }
      this.manualEdit = false
      this.copied = false
      this.rebuildMessage()
    },
    renderMessage() {
      return this.currentText.replace(/{{([1-9][0-9]*)}}/g, (match, key) => this.values[key]?.trim() || match)
    },
    rebuildMessage() { this.message = this.renderMessage(); this.manualEdit = false; this.copied = false },
    setValue(key, value) {
      this.$set(this.values, key, value)
      if (!this.manualEdit) this.message = this.renderMessage()
      this.copied = false
    },
    editMessage(value) { this.message = value; this.manualEdit = true; this.copied = false },
    async copy() {
      if (!this.canCopy) return
      this.copyError = ''
      try {
        await navigator.clipboard.writeText(this.message)
        this.copied = true
      } catch (error) {
        this.copyError = 'Could not copy the message. Please try again.'
      }
    },
    close() { this.$modal.hide(this.modalName) }
  }
}
</script>

<style scoped lang="scss">
.communication-template-copy { color: $ui-text; background: $ui-surface; font-family: $ui-font-body; }
.communication-template-copy__body { display: grid; gap: $ui-space-16; padding: $ui-space-24; }
.communication-template-copy__context { margin: 0; color: $ui-ink; font-weight: 600; }
.communication-template-copy__choices { display: grid; grid-template-columns: 2fr 1fr; gap: $ui-space-16; }
.communication-template-copy h3 { margin: 0; color: $ui-ink; font-size: $ui-font-size-section-title; }
.communication-template-copy__body ::v-deep textarea { min-height: 220px; resize: vertical; white-space: pre-wrap; }
.communication-template-copy__hint { margin: 0; color: $ui-muted; font-size: $ui-font-size-metadata; }
.communication-template-copy__variables-heading { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-8; }
.communication-template-copy__variables-heading button { border: 0; background: none; color: $ui-blue; cursor: pointer; }
.communication-template-copy__variable-table { display: grid; gap: $ui-space-8; }
.communication-template-copy__variable-row { display: grid; grid-template-columns: 1fr 1fr; align-items: center; gap: $ui-space-12; }
.communication-template-copy__variable-row div { display: flex; align-items: center; gap: $ui-space-8; }
.communication-template-copy__variable-row code { color: $ui-blue; }
.communication-template-copy__error { margin: 0; color: $ui-red; }
.communication-template-copy__footer { display: flex; justify-content: flex-end; gap: $ui-space-12; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; }
@media (max-width: 700px) { .communication-template-copy__choices, .communication-template-copy__variable-row { grid-template-columns: 1fr; } }
</style>
